// ─── Maturity Model ────────────────────────────────────────────────────────

import type { MaturityFramework, MaturityAssessment } from '../../graphql/types';
import { apiRequest } from './http';
import { createEntityCrudClient } from './crudFactory';

export interface CreateMaturityFrameworkInput {
  name: string;
  description?: string;
  version: string;
  status?: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface UpdateMaturityFrameworkInput {
  name?: string;
  description?: string;
  version?: string;
  status?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface MaturityResponseInput {
  capabilityId: string;
  level: number;
  comment?: string;
  // Backend `maturityResponseSchema` requires this (ISO 8601 datetime) on
  // every response — see MaturityAssessment.ts. Never optional there, so it
  // isn't optional here either; callers must stamp it at submission time.
  respondedAt: string;
}

export interface CreateMaturityAssessmentInput {
  name: string;
  description?: string;
  frameworkId: string;
  subjectEntityType: string;
  subjectEntityId: string;
  assessmentPeriod: string;
  dueDate: string;
  responses?: MaturityResponseInput[];
  // Backend `createMaturityAssessmentInputSchema` accepts only the literal
  // 'Team' (assessments are always team-owned, even for Service subjects —
  // see MaturityAssessmentRepository.createMaturityAssessment, which hardcodes
  // ownerType: 'Team' regardless of input). A loose `string` type here let a
  // caller send 'Service' and pass TypeScript while still 400ing at the API
  // boundary (issue #1567) — the literal type makes that a compile error instead.
  ownerType: 'Team';
  ownerId: string;
}

export interface UpdateMaturityAssessmentInput {
  responses?: MaturityResponseInput[];
  status?: string;
  description?: string;
}

const frameworkClient = createEntityCrudClient<
  MaturityFramework,
  CreateMaturityFrameworkInput,
  UpdateMaturityFrameworkInput
>('/maturity-frameworks');

export async function createMaturityFramework(
  input: CreateMaturityFrameworkInput
): Promise<MaturityFramework> {
  return frameworkClient.create(input);
}

export async function updateMaturityFramework(
  frameworkId: string,
  input: UpdateMaturityFrameworkInput
): Promise<MaturityFramework> {
  return frameworkClient.update(frameworkId, input);
}

export async function deleteMaturityFramework(frameworkId: string): Promise<void> {
  return frameworkClient.remove(frameworkId);
}

const assessmentClient = createEntityCrudClient<
  MaturityAssessment,
  CreateMaturityAssessmentInput,
  UpdateMaturityAssessmentInput
>('/maturity-assessments');

export async function createMaturityAssessment(
  input: CreateMaturityAssessmentInput
): Promise<MaturityAssessment> {
  return assessmentClient.create(input);
}

export async function updateMaturityAssessment(
  assessmentId: string,
  input: UpdateMaturityAssessmentInput
): Promise<MaturityAssessment> {
  return assessmentClient.update(assessmentId, input);
}

export async function deleteMaturityAssessment(assessmentId: string): Promise<void> {
  return assessmentClient.remove(assessmentId);
}

export async function approveMaturityAssessment(assessmentId: string): Promise<MaturityAssessment> {
  return apiRequest<MaturityAssessment>(`/maturity-assessments/${assessmentId}/approve`, {
    method: 'POST',
  });
}
